/**
 * API des COLLABORATEURS : les comptes de mon entreprise, gérés par l'éleveur ou
 * l'installateur titulaire.
 *
 * Toutes ces routes passent par USERS. La liste est scopée par DATA sur l'entreprise de la
 * session ; la création et la suppression dépendent des contrôles de USERS (voir
 * ISSUE_EN_COURS.md pour la création).
 */
import { I18nKey } from "../i18n/i18n";
import {
  AccountWithoutPassword,
  CreateAccountAddToCompany,
  UpdateAccount,
} from "../generated/types";
import { apiFetchJson, apiFetchRoute } from "./apiFetchRoute";
import { toUpdateAccount } from "./user";

export type CollaboratorForm = {
  first_name: string;
  last_name: string;
  email: string;
  phone_mobile: string;
  phone_fixe: string;
};

export const EMPTY_COLLABORATOR_FORM: CollaboratorForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone_mobile: "",
  phone_fixe: "",
};

/** Les comptes de l'entreprise, sans le mien, triés par nom puis prénom. */
export const collaboratorsOf = (
  accounts: AccountWithoutPassword[],
  ownId: number
): AccountWithoutPassword[] =>
  accounts
    .filter((account) => account.id !== ownId)
    .sort(
      (a, b) =>
        a.last_name.localeCompare(b.last_name) ||
        a.first_name.localeCompare(b.first_name)
    );

/**
 * Corps de POST /accounts/add. Le collaborateur hérite de l'entreprise et de la langue de
 * son créateur ; le type est choisi par USERS d'après le type du créateur.
 */
export const toCreateCollaborator = (
  form: CollaboratorForm,
  owner: AccountWithoutPassword
): CreateAccountAddToCompany => ({
  account: {
    language: owner.language,
    id_company: owner.id_company,
    email: form.email.trim(),
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    phone_mobile: form.phone_mobile.trim(),
    phone_fixe: form.phone_fixe.trim(),
  },
  owner_mail: owner.email,
});

export const toCollaboratorForm = (
  account: AccountWithoutPassword
): CollaboratorForm => ({
  first_name: account.first_name,
  last_name: account.last_name,
  email: account.email,
  phone_mobile: account.phone_mobile,
  phone_fixe: account.phone_fixe,
});

/**
 * Corps de PUT /accounts. L'email, l'entreprise, la langue et l'avatar restent ceux du
 * compte : le back semble retrouver le compte à modifier par son email.
 */
export const toUpdateCollaborator = (
  form: CollaboratorForm,
  account: AccountWithoutPassword
): UpdateAccount =>
  toUpdateAccount({
    ...account,
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    phone_mobile: form.phone_mobile.trim(),
    phone_fixe: form.phone_fixe.trim(),
  });

export const fetchCompanyAccounts = async (): Promise<{
  accounts: AccountWithoutPassword[];
  error: I18nKey | "";
}> => {
  try {
    // 204 : aucun compte, apiFetchJson renvoie alors undefined.
    const data = await apiFetchJson("getAccounts", undefined);
    return { accounts: data ?? [], error: "" };
  } catch (error) {
    console.error("Erreur lors du chargement des collaborateurs:", error);
    // i18n.t("collaborators-load-failed")
    return { accounts: [], error: "collaborators-load-failed" };
  }
};

/** Rend la clé du message d'erreur, vide en cas de succès. */
export const createCollaborator = async (
  form: CollaboratorForm,
  owner: AccountWithoutPassword
): Promise<I18nKey | ""> => {
  try {
    const response = await apiFetchRoute(
      "postAccountsAdd",
      toCreateCollaborator(form, owner)
    );
    if (response.ok) return "";
    // i18n.t("collaborator-create-failed")
    // i18n.t("register-error-email-exists")
    return response.status === 409
      ? "register-error-email-exists"
      : "collaborator-create-failed";
  } catch (error) {
    console.error("Erreur lors de la création du collaborateur:", error);
    return "collaborator-create-failed";
  }
};

/** Rend la clé du message d'erreur, vide en cas de succès. */
export const updateCollaborator = async (
  form: CollaboratorForm,
  account: AccountWithoutPassword
): Promise<I18nKey | ""> => {
  try {
    const response = await apiFetchRoute(
      "putAccounts",
      toUpdateCollaborator(form, account)
    );
    if (response.ok) return "";
    // i18n.t("collaborator-update-failed")
    return "collaborator-update-failed";
  } catch (error) {
    console.error("Erreur lors de la modification du collaborateur:", error);
    return "collaborator-update-failed";
  }
};

/** Rend la clé du message d'erreur, vide en cas de succès. */
export const deleteCollaborator = async (id: number): Promise<I18nKey | ""> => {
  try {
    const response = await apiFetchRoute("deleteAccountsId", undefined, {
      params: { id },
    });
    if (response.ok) return "";
    // i18n.t("collaborator-delete-failed")
    // i18n.t("collaborator-delete-forbidden")
    return response.status === 403
      ? "collaborator-delete-forbidden"
      : "collaborator-delete-failed";
  } catch (error) {
    console.error("Erreur lors de la suppression du collaborateur:", error);
    return "collaborator-delete-failed";
  }
};

/*
 * PARTAGE INTERNE : l'éleveur titulaire choisit les élevages ou bâtiments qu'un
 * collaborateur peut consulter. Partager un élevage couvre ses bâtiments, y compris ceux
 * ajoutés plus tard (résolution à la lecture côté DATA, comme pour les partenaires).
 *
 * Routes déclarées dans `parameters/routes.tsv`, pas encore implémentées par DATA (voir
 * ISSUE_EN_COURS.md). Contrat : GET renvoie { breedings: [{ id, name, shared, buildings:
 * [{ id, name, shared }] }] } ; POST prend { resource_type, id_resource }.
 */

export type CollaboratorShareResourceType = "breeding" | "building";

export type CollaboratorShareBuilding = {
  id: number;
  name: string;
  shared: boolean;
};

export type CollaboratorShareBreeding = {
  id: number;
  name: string;
  shared: boolean;
  buildings: CollaboratorShareBuilding[];
};

type CollaboratorSharesResponse = {
  breedings: CollaboratorShareBreeding[] | null;
};

/** Go sérialise une slice vide en `null` : on rend toujours des tableaux. */
export const toCollaboratorShares = (
  data: CollaboratorSharesResponse | undefined
): CollaboratorShareBreeding[] =>
  (data?.breedings ?? []).map((breeding) => ({
    ...breeding,
    buildings: breeding.buildings ?? [],
  }));

export const fetchCollaboratorShares = async (
  idAccount: number
): Promise<{ breedings: CollaboratorShareBreeding[]; error: I18nKey | "" }> => {
  try {
    const data = (await apiFetchJson(
      "getCollaboratorsId_accountShares",
      undefined,
      { params: { id_account: idAccount } }
    )) as CollaboratorSharesResponse | undefined;
    return { breedings: toCollaboratorShares(data), error: "" };
  } catch (error) {
    console.error("Erreur lors du chargement des partages:", error);
    // i18n.t("collaborator-share-load-failed")
    return { breedings: [], error: "collaborator-share-load-failed" };
  }
};

/** Partage ou retire une ressource. Rend la clé du message d'erreur, vide en cas de succès. */
export const setCollaboratorShare = async (
  idAccount: number,
  resourceType: CollaboratorShareResourceType,
  idResource: number,
  shared: boolean
): Promise<I18nKey | ""> => {
  try {
    const response = shared
      ? await apiFetchRoute(
          "postCollaboratorsId_accountShares",
          // Route déclarée `request: "x"` : le cast est nécessaire pour envoyer le corps.
          { resource_type: resourceType, id_resource: idResource } as never,
          { params: { id_account: idAccount } }
        )
      : await apiFetchRoute(
          "deleteCollaboratorsId_accountSharesResource_typeId_resource",
          undefined,
          {
            params: {
              id_account: idAccount,
              resource_type: resourceType,
              id_resource: idResource,
            },
          }
        );
    if (response.ok) return "";
    // i18n.t("collaborator-share-failed")
    return "collaborator-share-failed";
  } catch (error) {
    console.error("Erreur lors de la modification du partage:", error);
    return "collaborator-share-failed";
  }
};
